/**
 * Backend path segments (prefix with env.apiBaseUrl in the Axios client).
 * Mirrors mobile `apps/mobile/src/api/*` usage under `/api/v1`.
 */
export const endpoints = {
  auth: {
    login: '/api/v1/auth/login',
    signup: '/api/v1/auth/signup',
    verify: '/api/v1/auth/verify',
    resendVerification: '/api/v1/auth/resend-verification',
    forgotPassword: '/api/v1/auth/forgot-password',
    resetPassword: '/api/v1/auth/reset-password',
    refresh: '/api/v1/auth/refresh',
    me: '/api/v1/auth/me',
    logout: '/api/v1/auth/logout',
    studentSignup: '/api/v1/auth/student-signup',
  },
  profile: {
    me: '/api/v1/profile/me',
    mePhoto: '/api/v1/profile/me/photo',
    mentor: (mentorId: string) => `/api/v1/profile/mentor/${mentorId}`,
  },
  dashboard: {
    me: '/api/v1/dashboard/me',
    student: '/api/v1/dashboard/student',
    parent: '/api/v1/dashboard/parent',
    mentor: '/api/v1/dashboard/mentor',
  },
  lessons: {
    dashboard: (role: string) => `/api/v1/lessons/${role}/dashboard`,
    list: '/api/v1/lessons',
    detail: (lessonId: string) => `/api/v1/lessons/${lessonId}`,
    sessions: (lessonId: string) => `/api/v1/lessons/${lessonId}/sessions`,
  },
  sessions: {
    upcoming: '/api/v1/sessions/upcoming',
    detail: (sessionId: string) => `/api/v1/sessions/${sessionId}`,
    join: (sessionId: string) => `/api/v1/sessions/${sessionId}/join`,
    generateMeetingLink: (sessionId: string) =>
      `/api/v1/sessions/${sessionId}/generate-meeting-link`,
    mentorNote: (sessionId: string) => `/api/v1/sessions/${sessionId}/mentor-note`,
    quizzes: (sessionId: string) => `/api/v1/sessions/${sessionId}/quizzes`,
    aiSummary: (sessionId: string) => `/api/v1/sessions/${sessionId}/ai-summary`,
    aiSummaryGenerate: (sessionId: string) => `/api/v1/sessions/${sessionId}/ai-summary/generate`,
    transcriptStatus: (sessionId: string) => `/api/v1/sessions/${sessionId}/transcript/status`,
    transcript: (sessionId: string) => `/api/v1/sessions/${sessionId}/transcript`,
    transcriptConsent: (sessionId: string) => `/api/v1/sessions/${sessionId}/transcript/consent`,
    complete: (sessionId: string) => `/api/v1/sessions/${sessionId}/complete`,
    aiQuizGenerate: (sessionId: string) => `/api/v1/sessions/${sessionId}/ai-quiz/generate`,
    aiQuizMentor: (sessionId: string) => `/api/v1/sessions/${sessionId}/ai-quiz/mentor`,
    aiQuizStudent: (sessionId: string) => `/api/v1/sessions/${sessionId}/ai-quiz/student`,
  },
  aiQuizzes: {
    patch: (quizId: string) => `/api/v1/ai-quizzes/${quizId}`,
    publish: (quizId: string) => `/api/v1/ai-quizzes/${quizId}/publish`,
    attempt: (quizId: string) => `/api/v1/ai-quizzes/${quizId}/attempt`,
    result: (quizId: string) => `/api/v1/ai-quizzes/${quizId}/result`,
  },
  quizzes: {
    detail: (quizId: string) => `/api/v1/quizzes/${quizId}`,
  },
  messages: {
    threads: '/api/v1/messages/threads',
    thread: (threadId: string) => `/api/v1/messages/${threadId}`,
    send: '/api/v1/messages/send',
    read: '/api/v1/messages/read',
    unreadTotal: '/api/v1/messages/unread/total',
    lessonThread: '/api/v1/messages/lesson-thread',
  },
  notifications: {
    list: '/api/v1/notifications',
    read: (id: string) => `/api/v1/notifications/${id}/read`,
    readAll: '/api/v1/notifications/read-all',
    deviceTokens: '/api/v1/notifications/device-tokens',
  },
  wallet: {
    studentBalance: '/api/v1/wallet/balance',
  },
  parentWallet: {
    me: '/api/v1/parent-wallet/me',
    transactions: '/api/v1/parent-wallet/me/transactions',
    dummyTopup: '/api/v1/parent-wallet/me/dummy-topup',
  },
  mentorWallet: {
    me: '/api/v1/mentor-wallet/me',
    transactions: '/api/v1/mentor-wallet/me/transactions',
    payoutRequests: '/api/v1/mentor-wallet/me/payout-requests',
    payoutMethods: '/api/v1/mentor-wallet/me/payout-methods',
    payoutMethod: (id: string) => `/api/v1/mentor-wallet/me/payout-methods/${id}`,
    payoutMethodDefault: (id: string) => `/api/v1/mentor-wallet/me/payout-methods/${id}/default`,
    payoutMethodDeactivate: (id: string) =>
      `/api/v1/mentor-wallet/me/payout-methods/${id}/deactivate`,
    payoutRequest: (id: string) => `/api/v1/mentor-wallet/me/payout-requests/${id}`,
  },
  mentor: {
    availability: '/api/v1/mentor/availability',
    calendar: '/api/v1/mentor/calendar',
    verificationStatus: '/api/v1/mentor/verification-status',
    identityStart: '/api/v1/mentor/identity/start',
    studentVerificationUpload: '/api/v1/mentor/student-verification/upload',
    studentVerificationFiles: '/api/v1/mentor/student-verification/files',
    listings: '/api/v1/mentor/listings',
    listing: (listingId: string) => `/api/v1/mentor/listings/${listingId}`,
    listingStatus: (listingId: string) => `/api/v1/mentor/listings/${listingId}/status`,
  },
  education: {
    universities: '/api/v1/education/universities',
    subjects: '/api/v1/education/subjects',
    examSubjects: '/api/v1/education/exam-subjects',
  },
  search: {
    mentors: '/api/v1/search/mentors',
  },
  mentors: {
    search: '/api/v1/mentors/search',
    detail: (mentorId: string) => `/api/v1/mentors/${mentorId}`,
    publicProfile: (mentorId: string) => `/api/v1/mentors/${mentorId}/public-profile`,
    discoveryProfile: (mentorId: string) => `/api/v1/mentors/${mentorId}/profile`,
    contactRequest: (mentorId: string) => `/api/v1/mentors/${mentorId}/contact-request`,
    availability: (mentorId: string) => `/api/v1/mentors/${mentorId}/availability`,
    availabilitySummary: (mentorId: string) => `/api/v1/mentors/${mentorId}/availability-summary`,
    pricingSummary: (mentorId: string) => `/api/v1/mentors/${mentorId}/pricing-summary`,
    updatePricing: '/api/v1/mentors/me/pricing',
  },
  mentorProfileMedia: {
    photos: '/api/v1/mentor/profile/media/photos',
    photosUpload: '/api/v1/mentor/profile/media/photos/upload',
    photo: (photoId: string) => `/api/v1/mentor/profile/media/photos/${photoId}`,
    introVideo: '/api/v1/mentor/profile/media/intro-video',
  },
  mentorDiscovery: {
    feed: '/api/v1/mentor-discovery',
    recommended: '/api/v1/mentor-discovery/recommended',
    saved: '/api/v1/mentor-discovery/saved',
    like: (mentorId: string) => `/api/v1/mentor-discovery/${mentorId}/like`,
    dislike: (mentorId: string) => `/api/v1/mentor-discovery/${mentorId}/dislike`,
    save: (mentorId: string) => `/api/v1/mentor-discovery/${mentorId}/save`,
  },
  references: {
    submit: '/api/v1/teacher-references',
    mine: '/api/v1/my/teacher-references',
    mentorInbox: '/api/v1/mentor/me/references',
    mentorUpdateStatus: (referenceId: string) =>
      `/api/v1/mentor/me/references/${referenceId}/status`,
    publicForMentor: (mentorId: string) => `/api/v1/mentors/${mentorId}/references`,
    publicSummaryForMentor: (mentorId: string) => `/api/v1/mentors/${mentorId}/references/summary`,
  },
  mentorContactRequests: {
    list: '/api/v1/mentor-contact-requests/me',
    cancel: (requestId: string) => `/api/v1/mentor-contact-requests/${requestId}/cancel`,
    mentorList: '/api/v1/mentor/contact-requests',
    mentorAccept: (requestId: string) => `/api/v1/mentor/contact-requests/${requestId}/accept`,
    mentorReject: (requestId: string) => `/api/v1/mentor/contact-requests/${requestId}/reject`,
    adminList: '/api/v1/admin/mentor-contact-requests',
    adminDetail: (requestId: string) => `/api/v1/admin/mentor-contact-requests/${requestId}`,
    adminStatus: (requestId: string) => `/api/v1/admin/mentor-contact-requests/${requestId}/status`,
  },
  reports: {
    create: '/api/v1/reports',
    adminList: '/api/v1/admin/reports',
    adminDetail: (reportId: string) => `/api/v1/admin/reports/${reportId}`,
    adminMarkUnderReview: (reportId: string) =>
      `/api/v1/admin/reports/${reportId}/mark-under-review`,
    adminResolve: (reportId: string) => `/api/v1/admin/reports/${reportId}/resolve`,
    adminDismiss: (reportId: string) => `/api/v1/admin/reports/${reportId}/dismiss`,
  },
  listings: {
    public: (listingId: string) => `/api/v1/listings/${listingId}/public`,
    availableSlots: (listingId: string) => `/api/v1/listings/${listingId}/available-slots`,
    view: (listingId: string) => `/api/v1/listings/${listingId}/view`,
  },
  users: {
    me: '/api/v1/users/me',
    parents: '/api/v1/parents',
    students: '/api/v1/students',
  },
  bookings: {
    create: '/api/v1/bookings/create',
    checkout: (bookingId: string) => `/api/v1/bookings/${bookingId}/checkout`,
    list: '/api/v1/bookings',
    status: (bookingId: string) => `/api/v1/bookings/${bookingId}/status`,
  },
  checkout: {
    dummyPurchase: '/api/v1/checkout/dummy-purchase',
  },
  uploads: {
    presignGet: '/api/v1/uploads/presign-get',
  },
  favourites: {
    mentors: '/api/v1/favourites/mentors',
    mentor: (mentorId: string) => `/api/v1/favourites/mentors/${mentorId}`,
    listings: '/api/v1/favourites/listings',
    listing: (listingId: string) => `/api/v1/favourites/listings/${listingId}`,
    parentStudentMentors: (studentId: string) =>
      `/api/v1/parent/students/${studentId}/favourites/mentors`,
  },
  marketing: {
    sponsoredCards: '/api/v1/marketing/sponsored-cards',
    impression: (campaignId: string) =>
      `/api/v1/marketing/sponsored-cards/${campaignId}/impression`,
    click: (campaignId: string) => `/api/v1/marketing/sponsored-cards/${campaignId}/click`,
  },
  admin: {
    dashboard: '/api/v1/admin/dashboard',
    marketing: {
      campaigns: '/api/v1/admin/marketing/campaigns',
      campaign: (id: string) => `/api/v1/admin/marketing/campaigns/${id}`,
      campaignMetrics: (id: string) => `/api/v1/admin/marketing/campaigns/${id}/metrics`,
      overallMetrics: '/api/v1/admin/marketing/metrics',
    },
    mentorVerifications: {
      pending: '/api/v1/admin/mentor-verifications/pending',
      detail: (mentorId: string) => `/api/v1/admin/mentor-verifications/${mentorId}`,
      approve: (mentorId: string) => `/api/v1/admin/mentor-verifications/${mentorId}/approve`,
      reject: (mentorId: string) => `/api/v1/admin/mentor-verifications/${mentorId}/reject`,
    },
    mentorProfiles: {
      list: '/api/v1/admin/mentor-profiles',
      detail: (mentorId: string) => `/api/v1/admin/mentor-profiles/${mentorId}`,
      approve: (mentorId: string) => `/api/v1/admin/mentor-profiles/${mentorId}/approve`,
      requestChanges: (mentorId: string) =>
        `/api/v1/admin/mentor-profiles/${mentorId}/request-changes`,
      reject: (mentorId: string) => `/api/v1/admin/mentor-profiles/${mentorId}/reject`,
      hide: (mentorId: string) => `/api/v1/admin/mentor-profiles/${mentorId}/hide`,
      restore: (mentorId: string) => `/api/v1/admin/mentor-profiles/${mentorId}/restore`,
    },
    listings: {
      list: '/api/v1/admin/listings',
      detail: (listingId: string) => `/api/v1/admin/listings/${listingId}`,
      approve: (listingId: string) => `/api/v1/admin/listings/${listingId}/approve`,
      requestChanges: (listingId: string) => `/api/v1/admin/listings/${listingId}/request-changes`,
      reject: (listingId: string) => `/api/v1/admin/listings/${listingId}/reject`,
      hide: (listingId: string) => `/api/v1/admin/listings/${listingId}/hide`,
      restore: (listingId: string) => `/api/v1/admin/listings/${listingId}/restore`,
    },
    users: {
      list: '/api/v1/admin/users',
      detail: (userId: string) => `/api/v1/admin/users/${userId}`,
      suspend: (userId: string) => `/api/v1/admin/users/${userId}/suspend`,
      reactivate: (userId: string) => `/api/v1/admin/users/${userId}/reactivate`,
    },
  },
  materials: {
    mentor: {
      list: '/api/v1/mentor/materials',
      detail: (materialId: string) => `/api/v1/mentor/materials/${materialId}`,
    },
    student: {
      list: '/api/v1/student/materials',
      detail: (materialId: string) => `/api/v1/student/materials/${materialId}`,
      submit: (assignmentId: string) => `/api/v1/student/assignments/${assignmentId}/submission`,
    },
    parent: {
      list: (studentId: string) => `/api/v1/parent/children/${studentId}/materials`,
      detail: (studentId: string, materialId: string) =>
        `/api/v1/parent/children/${studentId}/materials/${materialId}`,
    },
  },
} as const;
